import { CaretDownIcon, CaretRightIcon } from '@phosphor-icons/react'
import { useEffect, useMemo, useState } from 'react'
import { useQuery } from 'urql'
import { Button } from 'app/components/ui/button'
import type { WorkflowRun } from 'app/generated/workflows'
import { logError } from 'lib/logging'
import { ContactSearchField } from './ContactSearchField'
import { filterPhrase, formatEventTimestamp } from 'app/lib/workflows/format'
import { WORKFLOW_REJECTIONS_QUERY, WORKFLOW_TRIGGER_EVENTS_QUERY } from 'app/lib/workflows/graphql'

/** The compiled trigger, as the ENGINE sees it — passed down from the builder's serializer so this
 *  panel diagnoses against the same filters the engine matches. */
export type TriggerSpec = {
  /** Event doc coordinates, e.g. type "Contact", action "updated". */
  eventType: string
  eventAction: string
  filters: { attribute: string; operator: string; value?: string }[]
  stateFilters?: { attribute: string; operator: string; value?: string }[]
  /** How the entry conditions combine. The engine keeps one logic for the whole list. */
  filterLogic?: `and` | `or`
  /** Contact-object triggers can scope the event list to one contact (typeId = contactId). */
  contactScoped: boolean
  /** The workflow's location. The evaluator only ever sees events from it, so listing another
   *  location's events here would invent mismatches that never happened. */
  locationId?: string
}

type EventHit = {
  id: string
  type: string
  typeId: string
  action: string
  eventDate: string
  createdDate: string
  source: string
  data: Record<string, unknown> | null
  workflowIds: string[]
}

const isRecentEvent = (createdDate: string) => Date.now() - Date.parse(createdDate) < 10 * 60 * 1000

/** What the event's change payload carried for one filter attribute, rendered compactly.
 *  Change payloads are `{ from, to }` per field; state attributes may be dotted paths. */
function eventValueFor(data: Record<string, unknown> | null, attribute: string): string {
  if (!data) return `(nothing)`
  const direct = data[attribute]
  if (direct === undefined) return `(not in this change)`
  if (direct && typeof direct === `object` && (`to` in direct || `from` in direct)) {
    const change = direct as { from?: unknown; to?: unknown }
    return `${JSON.stringify(change.from ?? null)} → ${JSON.stringify(change.to ?? null)}`
  }
  const text = JSON.stringify(direct)
  return text.length > 60 ? `${text.slice(0, 59)}…` : text
}

/** One event's verdict against this workflow, from the durable evidence the engine leaves today:
 *  a run (enrolled), the workflow's id stamped on the event without a run (matched then rejected),
 *  or no stamp at all (entry conditions didn't match, or the event predates the workflow). */
// The engine's recorded decline reasons (TRK-5581) rendered as sentences a practice can act on.
function rejectionPhrase(reason: string, spec: TriggerSpec): string {
  if (reason === `state_filters_not_met`) {
    const checks = spec.stateFilters?.length ? ` (${spec.stateFilters.map(filterPhrase).join(`; `)})` : ``
    return `An "only if" check wasn't true at that moment${checks}.`
  }
  if (reason === `no_contact_match`) {
    return `No contact could be matched to this event, and the trigger requires one.`
  }
  if (reason === `reenrollment_skipped`) {
    return `They were already in this workflow, and the re-enrollment policy says not to run it again.`
  }
  return `The engine declined it: ${reason.replace(/_/g, ` `)}.`
}

function Verdict({
  event,
  workflowId,
  runByEvent,
  reasonByEvent,
  triggerStartDate,
  spec,
}: {
  event: EventHit
  workflowId: string
  runByEvent: Map<string, WorkflowRun>
  reasonByEvent: Map<string, string>
  triggerStartDate?: string
  spec: TriggerSpec
}) {
  const run = runByEvent.get(event.id)
  if (run) {
    return <span className="text-[11px] font-medium text-emerald-700">Enrolled · run {run.status}</span>
  }
  if (event.workflowIds.includes(workflowId)) {
    const reason = reasonByEvent.get(event.id)
    return (
      <div className="space-y-1">
        <span className="text-[11px] font-medium text-amber-700">Matched, then declined</span>
        <p className="text-[11px] leading-relaxed text-subtle">
          {reason
            ? rejectionPhrase(reason, spec)
            : // Declines recorded before the engine kept reasons (TRK-5581, live 08-26) have only
              // the stamp, so the old reconstruction wording stays for that history.
              `The workflow saw this event and matched it, then didn't enroll: an "only if" check wasn't true at that moment${spec.stateFilters?.length ? ` (${spec.stateFilters.map(filterPhrase).join(`; `)})` : ``}, no contact could be matched, or they were already in the workflow.`}
        </p>
      </div>
    )
  }
  if (triggerStartDate && event.createdDate < triggerStartDate) {
    return <span className="text-[11px] text-minimal">Before this workflow&rsquo;s start date</span>
  }
  // A date-reached event only exists because the materializer already matched the date condition
  // (it never creates events that don't match), so its presence for this workflow IS the match.
  // With no run and no decline record it hasn't been enrolled yet — the honest verdict is "not
  // processed yet", never a filter mismatch. During the Sep-2026 queue stall this branch was the
  // gap: an old unstamped date event fell through to "didn't match" and blamed the filters for a
  // stalled queue (TRK-5760).
  if (spec.eventAction === `date_reached`) {
    return (
      <div className="space-y-1">
        <span className="text-[11px] font-medium text-subtle">Not processed yet</span>
        <p className="text-[11px] leading-relaxed text-subtle">
          The date condition was met and this event was created for the workflow, but it hasn&rsquo;t been enrolled yet.
          The trigger picks up new events every few minutes; a long delay points at the enrollment queue, not the
          filters.
        </p>
      </div>
    )
  }
  // Matching and enrollment are two queued hops a few minutes apart (evaluator cron, then a queue
  // worker), so a fresh unstamped event usually just hasn't been processed — claiming "didn't
  // match" here was a live falsehood caught 08-24, minutes before the same event enrolled.
  if (isRecentEvent(event.createdDate)) {
    return (
      <span className="text-[11px] text-minimal">
        Not processed yet · the trigger checks for new events every few minutes
      </span>
    )
  }
  // With no entry conditions "didn't match" is impossible; the honest causes are a location other
  // than this workflow's, or the evaluator never picking the event up.
  if (spec.filters.length === 0) {
    return (
      <div className="space-y-1">
        <span className="text-[11px] font-medium text-subtle">Not picked up</span>
        <p className="text-[11px] leading-relaxed text-subtle">
          This trigger has no entry conditions, so a mismatch can&rsquo;t be the reason. Most often the event belongs to
          a different location than this workflow&rsquo;s.
        </p>
      </div>
    )
  }
  return (
    <div className="space-y-1">
      <span className="text-[11px] font-medium text-subtle">Didn&rsquo;t match the entry conditions</span>
      <div className="space-y-0.5">
        {spec.filters.map((filter, index) => (
          <p key={index} className="text-[11px] leading-relaxed text-subtle">
            Condition: <span className="text-default">{filterPhrase(filter)}</span> · this event carried:{` `}
            <span className="font-mono text-[10px] text-default">{eventValueFor(event.data, filter.attribute)}</span>
          </p>
        ))}
      </div>
    </div>
  )
}

/** "Why didn't it fire?" — the observability hole named in the 08-24 ship review. Once a run
 *  exists the timeline answers everything; BEFORE a run exists the engine's decisions used to be
 *  invisible. Since TRK-5581 (api #599, verified 08-26) each decline leaves a skipped record with
 *  its exact reason; this panel shows those, and falls back to honest reconstruction for events
 *  from before the records existed. */
export function WhyDidntItFire({
  workflowId,
  triggerStartDate,
  spec,
  runs,
  timeZone,
}: {
  workflowId: string
  triggerStartDate?: string
  spec: TriggerSpec
  /** The panel's already-fetched runs — enrollment evidence, keyed by enrollmentEventId here. */
  runs: WorkflowRun[]
  timeZone?: string | null
}) {
  const [open, setOpen] = useState(false)
  const [contactId, setContactId] = useState(``)

  const runByEvent = useMemo(() => {
    const map = new Map<string, WorkflowRun>()
    for (const run of runs) if (run.enrollmentEventId) map.set(run.enrollmentEventId, run)
    return map
  }, [runs])

  const search = [
    `type:${spec.eventType}`,
    `action:${spec.eventAction}`,
    ...(spec.locationId ? [`locationId:${spec.locationId}`] : []),
    ...(spec.contactScoped && contactId ? [`typeId:${contactId}`] : []),
  ].join(` AND `)

  const [result] = useQuery<{ events: { hits: EventHit[]; total: number } }>({
    query: WORKFLOW_TRIGGER_EVENTS_QUERY,
    variables: { search, pagination: { limit: 10 }, sort: { field: `createdDate`, direction: `DESC` } },
    pause: !open,
    requestPolicy: `cache-and-network`,
  })
  const hits = result.data?.events?.hits ?? []

  // The engine's own decline records (TRK-5581): run-less skipped entries keyed by eventId.
  const [rejections] = useQuery<{
    workflowRunEvents: { hits: { eventId?: string | null; outputJson?: string | null }[] }
  }>({
    query: WORKFLOW_REJECTIONS_QUERY,
    variables: {
      search: `workflowId:${workflowId} AND status:skipped`,
      pagination: { limit: 50 },
      sort: { field: `createdDate`, direction: `DESC` },
    },
    pause: !open,
    requestPolicy: `cache-and-network`,
  })
  const reasonByEvent = useMemo(() => {
    const map = new Map<string, string>()
    for (const hit of rejections.data?.workflowRunEvents?.hits ?? []) {
      if (!hit.eventId || map.has(hit.eventId)) continue
      try {
        const reason = (JSON.parse(hit.outputJson ?? `{}`) as { reason?: string }).reason
        if (reason) map.set(hit.eventId, reason)
      } catch (error) {
        // Falls back to the reconstruction wording rather than dropping the row.
        logError(`Unparsable workflow rejection record`, error)
      }
    }
    return map
  }, [rejections.data])

  const queryError = result.error ?? rejections.error
  useEffect(() => {
    if (result.error) logError(`Why-didn't-it-fire: trigger event query failed`, result.error)
  }, [result.error])
  useEffect(() => {
    if (rejections.error) logError(`Why-didn't-it-fire: rejection query failed`, rejections.error)
  }, [rejections.error])

  return (
    <div className="border-b border-default">
      <Button variant="ghost" size="row" onClick={() => setOpen((v) => !v)}>
        {open ? <CaretDownIcon size={12} weight="bold" /> : <CaretRightIcon size={12} weight="bold" />}
        <span className="text-xs font-medium text-subtle">Why didn&rsquo;t it fire?</span>
      </Button>
      {open && (
        <div className="space-y-2 px-4 pb-3">
          {spec.contactScoped && (
            <ContactSearchField
              value={contactId}
              onChange={(id) => setContactId(id)}
              locationId={spec.locationId}
              placeholder="Narrow to one contact…"
            />
          )}
          {result.fetching && hits.length === 0 && <p className="text-[11px] text-minimal">Loading events…</p>}
          {/* An error must never read as "nothing happened" — that is the failure this panel exists
              to stop, applied to the panel itself. */}
          {queryError && (
            <p className="rounded-md bg-red-50 px-2 py-1.5 text-[11px] leading-relaxed text-red-700">
              Couldn&rsquo;t load the trigger history, so this list may be incomplete. Try again in a moment.
            </p>
          )}
          {!queryError && !result.fetching && hits.length === 0 && (
            <p className="text-[11px] leading-relaxed text-subtle">
              No {spec.eventType.toLowerCase()} {spec.eventAction.replace(/_/g, ` `)} events recorded
              {spec.contactScoped && contactId ? ` for this contact` : ``}. A change that shows nothing here never
              produced an event; for contacts, only certain fields do.
            </p>
          )}
          <div className="divide-y divide-subtle">
            {hits.map((event) => (
              <div key={event.id} className="py-2 first:pt-0">
                <div className="mb-1 flex items-center justify-between gap-2">
                  <span className="text-[11px] text-subtle">
                    {formatEventTimestamp(event.createdDate, false, timeZone)} · {event.source}
                  </span>
                  <span className="truncate font-mono text-[9px] text-minimal" title={event.typeId}>
                    {event.typeId.slice(-6)}
                  </span>
                </div>
                <Verdict
                  event={event}
                  workflowId={workflowId}
                  runByEvent={runByEvent}
                  reasonByEvent={reasonByEvent}
                  triggerStartDate={triggerStartDate}
                  spec={spec}
                />
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}
